import { useState, useEffect, useRef } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { CreditCostPill } from '@/components/ui/credit-cost-pill';
import useSWR from 'swr';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface ContactListOption {
  id: number;
  name: string;
  memberCount: number;
  campaignId: string | null;
}

export interface ProspectForCampaign {
  id: string;
  enrichmentStatus?: 'not_enriched' | 'enriching' | 'enriched' | 'failed' | 'skipped';
  enrichedEmail?: string;
}

interface AddToCampaignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prospects: ProspectForCampaign[];
  onSuccess?: () => void;
  defaultCampaignId?: string;
  defaultEnrichEmails?: boolean;
}

type CompletionState = null | 'success' | 'partial' | 'error' | 'limit_exceeded';

interface ResultState {
  status: CompletionState;
  message: string;
  details?: string;
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include' }).then((res) => res.json());

async function addProspectsToContactList(
  listId: number,
  prospectIds: string[],
  skipEnrichment = false
): Promise<{ success: boolean; added: number }> {
  // First add prospects to contacts table
  const contactsRes = await fetch('/api/contacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ prospectIds, allowWithoutEmail: skipEnrichment }),
  });
  if (!contactsRes.ok) {
    const error = await contactsRes.json();
    throw new Error(error.message || error.error || 'Failed to add to contacts');
  }
  const contacts = await contactsRes.json();
  const contactIds = (Array.isArray(contacts) ? contacts : contacts.contacts || []).map((c: any) => c.id);

  if (contactIds.length === 0) {
    return { success: true, added: 0 };
  }

  // Then add contacts to the list
  const listRes = await fetch(`/api/contact-lists/${listId}/contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ contactIds }),
  });
  if (!listRes.ok) {
    const error = await listRes.json();
    throw new Error(error.message || error.error || 'Failed to add to list');
  }
  return listRes.json();
}


export function AddToCampaignModal({
  open,
  onOpenChange,
  prospects,
  onSuccess,
  defaultCampaignId,
  defaultEnrichEmails = false,
}: AddToCampaignModalProps) {
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [enrichEmails, setEnrichEmails] = useState(defaultEnrichEmails);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichmentProgress, setEnrichmentProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<ResultState>({ status: null, message: '' });
  const abortRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();

  const { data: contactLists, isLoading: listsLoading } = useSWR<ContactListOption[]>(
    open ? '/api/contact-lists' : null,
    fetcher
  );

  useEffect(() => {
    if (open) {
      setSelectedListId(defaultCampaignId || '');
      setEnrichEmails(defaultEnrichEmails);
      setIsEnriching(false);
      setEnrichmentProgress({ done: 0, total: 0 });
      setResult({ status: null, message: '' });
      abortRef.current = null;
    }
  }, [open]);

  const availableLists = Array.isArray(contactLists) ? contactLists : [];

  const handleSubmit = async () => {
    if (!selectedListId) {
      setResult({ status: 'error', message: 'Please select a list' });
      return;
    }

    setResult({ status: null, message: '' });

    if (!enrichEmails) {
      // Push directly without enrichment
      setIsSubmitting(true);
      try {
        const allIds = prospects.map(p => p.id);
        const apiResult = await addProspectsToContactList(parseInt(selectedListId), allIds, true);
        setResult({
          status: 'success',
          message: `Added ${apiResult.added} contact${apiResult.added !== 1 ? 's' : ''} to list`,
        });
        onSuccess?.();
      } catch (error) {
        setResult({
          status: 'error',
          message: 'Failed to add to list',
          details: error instanceof Error ? error.message : 'An unexpected error occurred.',
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Enrich then push
    const controller = new AbortController();
    abortRef.current = controller;
    setIsEnriching(true);
    setEnrichmentProgress({ done: 0, total: prospects.length });

    const enrichedIds: string[] = [];
    let aborted = false;

    for (let i = 0; i < prospects.length; i++) {
      if (controller.signal.aborted) { aborted = true; break; }
      const prospect = prospects[i];

      if (prospect.enrichmentStatus === 'enriched' && prospect.enrichedEmail) {
        enrichedIds.push(prospect.id);
      } else {
        try {
          const startRes = await fetch('/api/people/enrich-async', {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prospectId: prospect.id }),
          });
          if (!startRes.ok) throw new Error('Failed to start enrichment');
          const { jobId } = await startRes.json();

          const enrichResult = await new Promise<any>((resolve) => {
            const poll = async () => {
              try {
                const res = await fetch(`/api/people/enrich-status/${jobId}`, { credentials: 'include' });
                const data = await res.json();
                if (data.done) { resolve(data.result || { success: false }); return; }
              } catch {}
              setTimeout(poll, 2000);
            };
            poll();
          });

          if (enrichResult.success && enrichResult.email) {
            enrichedIds.push(prospect.id);
          }
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') { aborted = true; break; }
        }
      }
      setEnrichmentProgress({ done: i + 1, total: prospects.length });
    }

    setIsEnriching(false);
    abortRef.current = null;

    if (aborted) {
      setResult({ status: 'error', message: 'Enrichment cancelled' });
      return;
    }

    if (enrichedIds.length === 0) {
      setResult({ status: 'error', message: 'No emails found for any prospects' });
      return;
    }

    setIsSubmitting(true);
    try {
      const apiResult = await addProspectsToContactList(parseInt(selectedListId), enrichedIds);
      const failedCount = prospects.length - enrichedIds.length;
      setResult({
        status: failedCount > 0 ? 'partial' : 'success',
        message: `Added ${apiResult.added} contact${apiResult.added !== 1 ? 's' : ''} to list`,
        details: failedCount > 0 ? `${failedCount} could not be enriched.` : undefined,
      });
      onSuccess?.();
    } catch (error) {
      setResult({
        status: 'error',
        message: 'Failed to add to list',
        details: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isEnriching && abortRef.current) {
      abortRef.current.abort();
      return;
    }
    onOpenChange(false);
  };

  const handleCreateList = () => {
    onOpenChange(false);
    navigate('/dashboard/lists');
  };

  const prospectCount = prospects.length;
  const needsEnrichmentCount = prospects.filter(p => p.enrichmentStatus !== 'enriched' || !p.enrichedEmail).length;
  const isProcessing = isEnriching || isSubmitting;
  const isComplete = result.status !== null;
  const isSuccessful = result.status === 'success' || result.status === 'partial';

  const getResultIcon = () => {
    switch (result.status) {
      case 'success':
        return <CheckCircleIcon className="h-12 w-12 text-success-foreground" />;
      case 'partial':
        return <ExclamationTriangleIcon className="h-12 w-12 text-warning-foreground" />;
      case 'error':
      case 'limit_exceeded':
        return <ExclamationCircleIcon className="h-12 w-12 text-danger-foreground" />;
      default:
        return null;
    }
  };

  const getResultColorClass = () => {
    switch (result.status) {
      case 'success':
        return 'bg-success-surface-strong border-success-border';
      case 'partial':
        return 'bg-warning-surface-strong border-warning-border';
      case 'error':
      case 'limit_exceeded':
        return 'bg-danger-surface-strong border-danger-border';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={isSubmitting ? undefined : handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Push to List</DialogTitle>
          {!isComplete && (
            <DialogDescription className="text-foreground">
              Add {prospectCount} prospect{prospectCount !== 1 ? 's' : ''} to a list.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="py-4">
          {isComplete ? (
            <div className={`rounded-lg p-6 text-center ${getResultColorClass()}`}>
              <div className="flex justify-center mb-3">{getResultIcon()}</div>
              <p className="font-normal text-foreground text-sm">{result.message}</p>
              {result.details && (
                <p className="text-sm text-foreground mt-1">{result.details}</p>
              )}
            </div>
          ) : (
            <>
              {listsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <span className="text-foreground">Loading lists...</span>
                </div>
              ) : availableLists.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-foreground mb-4">
                    You don't have any lists yet. Create a campaign to get started.
                  </p>
                  <Button onClick={handleCreateList} variant="secondary">
                    View Lists
                  </Button>
                </div>
              ) : (
                <>
                  <Select
                    value={selectedListId}
                    onValueChange={setSelectedListId}
                    disabled={isProcessing}
                  >
                    <SelectTrigger className="h-12 w-full truncate" inlineLabel="List">
                      <SelectValue placeholder="Select a list" />
                    </SelectTrigger>
                    <SelectContent className="w-[var(--radix-select-trigger-width)]">
                      {availableLists.map((list) => (
                        <SelectItem key={list.id} value={String(list.id)}>
                          {list.name.length > 55 ? list.name.slice(0, 55) + '...' : list.name}
                          {' '}({list.memberCount})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {!isEnriching && (
                    <div className="mt-4 flex items-center gap-0.5">
                      <Checkbox
                        id="enrich-emails"
                        checked={enrichEmails}
                        onCheckedChange={(checked) => setEnrichEmails(!!checked)}
                        disabled={isProcessing}
                      />
                      <label htmlFor="enrich-emails" className="text-sm text-foreground cursor-pointer mr-2">
                        Enrich emails
                      </label>
                      {needsEnrichmentCount > 0 && (
                        <span className="scale-75 origin-left"><CreditCostPill amount={2} suffix="prospect" /></span>
                      )}
                    </div>
                  )}

                  {isEnriching && (
                    <div className="mt-4">
                      <div className="h-2 bg-surface-track rounded-full overflow-hidden">
                        <div
                          className="h-full bg-info-solid transition-all duration-300"
                          style={{ width: `${(enrichmentProgress.done / enrichmentProgress.total) * 100}%` }}
                        />
                      </div>
                      <p className="text-sm text-foreground mt-2 text-center">
                        Finding email {enrichmentProgress.done} of {enrichmentProgress.total}...
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {isComplete ? (
            <Button variant="primary" onClick={handleClose}>
              {isSuccessful ? 'Done' : 'Close'}
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!selectedListId || isProcessing || availableLists.length === 0}
              >
                {isEnriching ? `Finding emails... (${enrichmentProgress.done}/${enrichmentProgress.total})`
                  : isSubmitting ? 'Adding...'
                  : enrichEmails ? `Find Email${prospectCount !== 1 ? 's' : ''} & Push`
                  : 'Push to List'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
